/**
 * Search Trigger Component
 * Replaces the dumb search input with a clickable trigger
 */

import { useTranslation, useDispatch } from '@spcc/core'
import { cn, Search } from '@spcc/uikit'
import { globalSearchActions } from '../../store/global-search.slice'

interface SearchTriggerProps {
  isRTL?: boolean
  className?: string
}

function getShortcutModifier(): string {
  if (typeof navigator === 'undefined') return 'Ctrl'
  const platform = navigator.platform || navigator.userAgent || ''
  return /(Mac|iPhone|iPad|iPod)/i.test(platform) ? '⌘' : 'Ctrl'
}

export function SearchTrigger({ isRTL, className }: SearchTriggerProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const handleClick = () => {
    dispatch(globalSearchActions.openSearch())
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      dispatch(globalSearchActions.openSearch())
    }
  }

  const iconPosition = isRTL ? 'end-4' : 'start-4'
  const inputPadding = isRTL ? 'pe-11' : 'ps-11'

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn('relative w-full max-w-md text-start', className)}
    >
      <Search
        className={cn(
          'absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none',
          iconPosition
        )}
      />
      <div
        className={cn(
          'h-11 w-full bg-secondary/50 dark:bg-secondary/80 border-0 rounded-xl',
          'flex items-center transition-[background-color,color] cursor-pointer',
          'hover:bg-secondary/70 dark:hover:bg-secondary/90',
          'focus:ring-2 focus:ring-primary/20',
          inputPadding
        )}
      >
        <span className="text-muted-foreground text-sm">
          {t('header.search')}
        </span>
        <kbd
          className={cn(
            'pointer-events-none hidden h-9 select-none items-center gap-2 rounded-xl border',
            'border-border/70 bg-background/95 px-2.5 shadow-sm text-sm text-muted-foreground',
            'sm:flex',
            isRTL ? 'me-auto ms-2' : 'ms-auto me-2'
          )}
          aria-label={t('header.search')}
        >
          <span className="inline-flex min-w-8 items-center justify-center rounded-md border border-border/70 bg-muted px-1.5 py-1 text-lg font-bold leading-none">
            {getShortcutModifier()}
          </span>
          <span className="inline-flex min-w-7 items-center justify-center rounded-md border border-border/70 bg-muted px-1.5 py-1 font-semibold leading-none">
            K
          </span>
        </kbd>
      </div>
    </button>
  )
}
